import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Copy, Linkedin, Twitter, ArrowRight, Download } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import MagneticButton from "@/components/site/MagneticButton";
import { getInsight, getRelatedInsights, subscribeNewsletter } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const WHITEPAPER_MAP = {
  "zero-trust-for-bfsi-2026": "zero-trust-bfsi-playbook.pdf",
  "mdr-vs-edr-what-you-actually-buy": "mdr-buyers-guide-2026.pdf",
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
}

function parseMd(md = "") {
  // Very lightweight markdown → block list; extracts h2 headings for TOC
  const blocks = [];
  const toc = [];
  const paragraphs = md.split(/\n\n+/);
  paragraphs.forEach((p) => {
    const t = p.trim();
    if (!t) return;
    if (t.startsWith("## ")) {
      const text = t.replace(/^##\s+/, "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      toc.push({ id, text });
      blocks.push({ type: "h2", id, text });
    } else if (t.startsWith("### ")) {
      blocks.push({ type: "h3", text: t.replace(/^###\s+/, "") });
    } else if (t.startsWith("- ") || t.startsWith("* ")) {
      blocks.push({ type: "ul", items: t.split("\n").map((l) => l.replace(/^[-*]\s+/, "")) });
    } else if (/^\d+\.\s/.test(t)) {
      blocks.push({ type: "ol", items: t.split("\n").map((l) => l.replace(/^\d+\.\s+/, "")) });
    } else {
      blocks.push({ type: "p", text: t });
    }
  });
  return { blocks, toc };
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [ins, setIns] = useState(null);
  const [related, setRelated] = useState([]);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState("");
  const [email, setEmail] = useState("");
  const [subBusy, setSubBusy] = useState(false);

  useEffect(() => {
    setIns(null);
    getInsight(slug).then(setIns).catch(() => setIns({ notFound: true }));
    getRelatedInsights(slug).then(setRelated).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const pc = Math.max(0, Math.min(1, (h.scrollTop) / Math.max(1, h.scrollHeight - h.clientHeight)));
      setProgress(pc);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  const parsed = useMemo(() => parseMd(ins?.body_md || ""), [ins]);

  useEffect(() => {
    if (!parsed.toc.length) return;
    const onScroll = () => {
      for (let i = parsed.toc.length - 1; i >= 0; i--) {
        const el = document.getElementById(parsed.toc[i].id);
        if (el && el.getBoundingClientRect().top < 180) {
          setActive(parsed.toc[i].id);
          return;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [parsed.toc]);

  useSEO({
    title: ins?.seo_title || (ins?.title ? `${ins.title} | RockLadder Insights` : "Insight"),
    description: ins?.seo_description || ins?.excerpt,
    canonical: `https://rockladder.com/insights/${slug}`,
    jsonLd: ins && !ins.notFound && {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: ins.title,
      description: ins.excerpt,
      author: { "@type": "Person", name: ins.author_name },
      datePublished: ins.published_at,
      publisher: { "@type": "Organization", name: "RockLadder Technologies" },
    },
  });

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareLinkedIn = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const shareX = `https://twitter.com/intent/tweet?text=${encodeURIComponent(ins?.title || "")}&url=${encodeURIComponent(shareUrl)}`;

  const copyLink = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    }
  };

  const wp = WHITEPAPER_MAP[slug];
  const wpUrl = wp ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/whitepapers/${wp}` : null;

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    setSubBusy(true);
    try {
      await subscribeNewsletter(email, `insight:${slug}`);
      toast.success("Subscribed.");
      setEmail("");
    } catch { toast.error("Subscription failed."); } finally { setSubBusy(false); }
  };

  if (ins?.notFound) {
    return <div className="pt-40 pb-24 text-center"><Link to="/insights" className="text-gold">Back to Insights</Link></div>;
  }
  if (!ins) return <div className="pt-40 pb-24 text-center text-inkText-muted">Loading…</div>;

  return (
    <>
      {/* Reading progress */}
      <div className="fixed top-[72px] left-0 right-0 z-40 h-[2px] bg-transparent">
        <div
          data-testid="reading-progress"
          className="h-full bg-gradient-to-r from-gold via-gold to-violet2"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <PageHero
        eyebrow={ins.category.toUpperCase()}
        title={ins.title}
        subtitle={ins.excerpt}
        breadcrumb={[{ label: "Insights", to: "/insights" }, { label: ins.title }]}
      />

      <section className="relative py-12">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-10">
          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-4">CONTENTS</div>
              <ul className="space-y-2">
                {parsed.toc.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      data-testid={`insight-toc-${t.id}`}
                      className={`block text-xs leading-snug transition-colors ${
                        active === t.id ? "text-gold" : "text-inkText-muted hover:text-inkText-primary"
                      }`}
                    >
                      {t.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Body */}
          <article className="max-w-3xl">
            <div className="flex items-center gap-4 text-xs text-inkText-muted font-mono tracking-wider mb-8">
              <span>{fmtDate(ins.published_at)}</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {ins.read_time_min} MIN READ
              </span>
              <span>·</span>
              <span>{ins.author_name}</span>
            </div>

            {parsed.blocks.map((b, i) => {
              if (b.type === "h2") return <h2 key={i} id={b.id} className="font-display text-2xl md:text-3xl text-inkText-primary mt-12 first:mt-0 scroll-mt-24">{b.text}</h2>;
              if (b.type === "h3") return <h3 key={i} className="font-display text-lg md:text-xl text-inkText-primary mt-8">{b.text}</h3>;
              if (b.type === "ul") return (
                <ul key={i} className="my-4 space-y-2">
                  {b.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-inkText-muted leading-relaxed"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" /><span>{it}</span></li>
                  ))}
                </ul>
              );
              if (b.type === "ol") return (
                <ol key={i} className="my-4 space-y-2 list-decimal list-inside text-inkText-muted marker:text-gold">
                  {b.items.map((it, j) => <li key={j} className="leading-relaxed">{it}</li>)}
                </ol>
              );
              return <p key={i} className="my-4 text-inkText-muted leading-[1.8]">{b.text}</p>;
            })}

            {/* Whitepaper download */}
            {wpUrl && (
              <div className="mt-10 rounded-2xl glass-panel-strong p-6 flex items-center justify-between gap-4">
                <div>
                  <div className="font-display text-lg text-inkText-primary">Download this as a whitepaper</div>
                  <p className="text-sm text-inkText-muted mt-1">Branded PDF · ready to share with your team.</p>
                </div>
                <a
                  href={wpUrl}
                  data-testid="download-whitepaper"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-[#FFD37A] to-[#C89026] text-ink px-4 py-2 text-sm font-medium border border-gold/60"
                >
                  <Download className="h-4 w-4" /> Whitepaper
                </a>
              </div>
            )}

            {/* Inline newsletter CTA */}
            <div className="mt-10 rounded-2xl glass-panel p-6">
              <div className="font-display text-lg text-inkText-primary mb-2">
                Get essays like this in your inbox.
              </div>
              <p className="text-xs text-inkText-muted mb-4">One considered essay per week. No filler.</p>
              <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Work email"
                  required
                  data-testid="insight-newsletter-email"
                  className="flex-1 rounded-full bg-ink/60 border border-gold/20 px-4 py-2.5 text-sm text-inkText-primary focus:outline-none focus:border-gold"
                />
                <button
                  disabled={subBusy}
                  data-testid="insight-newsletter-submit"
                  className="rounded-full bg-gradient-to-b from-[#FFD37A] to-[#C89026] text-ink px-5 py-2.5 text-sm font-medium border border-gold/60 disabled:opacity-50"
                >
                  {subBusy ? "…" : "Subscribe"}
                </button>
              </form>
            </div>
          </article>

          {/* Share + author */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-6">
              <div className="glass-panel rounded-2xl p-5">
                <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-3">SHARE</div>
                <div className="flex items-center gap-2">
                  <a href={shareLinkedIn} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 hover:border-gold text-inkText-primary hover:text-gold"><Linkedin className="h-3.5 w-3.5" /></a>
                  <a href={shareX} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 hover:border-gold text-inkText-primary hover:text-gold"><Twitter className="h-3.5 w-3.5" /></a>
                  <button onClick={copyLink} data-testid="share-copy" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 hover:border-gold text-inkText-primary hover:text-gold"><Copy className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="glass-panel rounded-2xl p-5">
                <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-3">AUTHOR</div>
                <div className="font-display text-base text-inkText-primary">{ins.author_name}</div>
                <div className="font-mono text-[10px] tracking-[0.22em] text-inkText-muted mt-1">{ins.author_role?.toUpperCase()}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="relative py-16 border-t border-gold/10">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-10">
              Related reading
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/insights/${r.slug}`} data-testid={`related-insight-${r.slug}`} className="glass-panel rounded-2xl p-6 hover:border-gold transition-colors">
                  <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body">{r.category.toUpperCase()}</div>
                  <div className="mt-2 font-display text-base text-inkText-primary line-clamp-2">{r.title}</div>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs text-inkText-muted"><ArrowRight className="h-3 w-3" /> READ</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
